import type { View } from '@/modules/Editor/views/view'

import type { Bounds, BoundsPerLabelIndex, RasterDataSnapshot } from './Raster'
import { Raster } from './Raster'
import type { RasterBufferAccessor } from './RasterBufferAccessor'
import { RasterTypes } from './rasterTypes'
import { encodeDenseRLE } from '@/modules/Editor/plugins/mask/rle/denseRle'
import { toRaw } from 'vue'
import { mark } from '@/performance/mark'
import {
  RASTER_DELETE_LABEL_FROM_RASTER_END,
  RASTER_DELETE_LABEL_FROM_RASTER_START,
} from '@/performance/keys'
import { checkBrowserSupportsSharedBuffer } from '@/core/utils/browser'
import { combineBounds } from '@/modules/Editor/plugins/mask/utils/shared/combineBounds'
import { BrushWorker } from '@/modules/Editor/workers/BrushWorker/BrushWorker'
import { isMaskAnnotation } from '@/modules/Editor/models/annotation/annotationKindValidator'
import { isMaskAnnotationEmpty } from '@/modules/Editor/plugins/mask/utils/shared/isMaskAnnotationEmpty'

export const EMPTY_PIXEL = 0

/**
 * An object which contains the raw data used to display a raster mask.
 * Each raster has its own id and references a particular file to which
 * it is associated. The size of the raster is automatically derived
 * from the file it is associated with.
 */
export class ImageRaster extends Raster {
  public readonly type: RasterTypes = RasterTypes.IMAGE
  private buffer: Uint8Array
  private sharedBuffer: SharedArrayBuffer | null = null
  private boundsPerLabelIndex: BoundsPerLabelIndex = {}

  constructor(view: View) {
    super(view)

    if (checkBrowserSupportsSharedBuffer()) {
      this.sharedBuffer = new SharedArrayBuffer(this.size)
      this.buffer = new Uint8Array(this.sharedBuffer)
    } else {
      this.buffer = new Uint8Array(this.size)
    }
  }

  public getLabelRange(): [number, number] {
    return [0, 0]
  }

  /**
   * For serialization, we need the actual buffer. For all other uses in app,
   * we should use the rasterBufferAccessors provided by getActiveBufferForRender
   * and getActiveBufferForEdit.
   */
  public getBufferForSerialization(): Uint8Array {
    return this.buffer
  }

  public setBoundsForLabelIndex(labelIndex: number, bounds: Bounds): void {
    this.boundsPerLabelIndex[labelIndex] = bounds
  }

  public getBoundsForLabelIndex(labelIndex: number): Bounds {
    return this.boundsPerLabelIndex[labelIndex]
  }

  protected deleteBoundsForLabelIndex(labelIndex: number): void {
    delete this.boundsPerLabelIndex[labelIndex]
  }

  public getBoundsForLabels(labelIndexes: number[]): Bounds | undefined {
    const bounds: Bounds[] = labelIndexes.map((labelIndex) =>
      this.getBoundsForLabelIndex(labelIndex),
    )
    return combineBounds(bounds)
  }

  public async mergeLabels(labelIndex1: number, ...labelIndexes: number[]): Promise<void> {
    const changedAnnotationIds: string[] = []
    for (const labelIndex of labelIndexes) {
      const bounds = this.getBoundsForLabelIndex(labelIndex)
      if (!bounds) {
        continue
      }

      await this.updateLabelOnRaster(labelIndex, labelIndex1)

      const mergedAnnotationId = this.getAnnotationMapping(labelIndex)
      if (mergedAnnotationId) {
        changedAnnotationIds.push(mergedAnnotationId)
      }
      this.deleteBoundsForLabelIndex(labelIndex)
      this.deleteAnnotationMapping(labelIndex)
    }

    this.invalidateAll(changedAnnotationIds)
  }

  public getActiveBufferForEdit(): RasterBufferAccessor {
    const { buffer, sharedBuffer, width, height } = this

    return {
      get(x: number, y: number): number {
        return buffer[y * width + x]
      },
      set(x: number, y: number, val: number): void {
        buffer[y * width + x] = val
      },
      getBuffer(): Uint8Array {
        return buffer
      },
      getSharedBuffer(): SharedArrayBuffer | ArrayBuffer {
        return sharedBuffer || buffer.buffer
      },
      width,
      height,
    }
  }

  /**
   * For ImageRasters there is only ever one buffer, so this
   * is just pass through to satisfy the generic interface.
   */
  public getActiveBufferForRender(): RasterBufferAccessor {
    return this.getActiveBufferForEdit()
  }

  /**
   * Returns a sub-buffer of the raster that is invalidated.
   * This is used to partially update the raster in WebGL.
   *
   * @returns A sub-buffer of the raster that is invalidated, or null if there is no invalidation.
   */
  public getInvalidatedSubBuffer(): {
    x: number
    y: number
    width: number
    height: number
    data: Uint8Array
  } | null {
    if (!this.isInvalidated() || !this._invalidatedRegion) {
      return null
    }

    const { xMin, yMin, xMax, yMax } = this._invalidatedRegion
    const regionWidth = xMax - xMin + 1
    const regionHeight = yMax - yMin + 1

    if (regionWidth <= 0 || regionHeight <= 0) {
      return null // Should not happen if invalidate logic is correct
    }

    const subBuffer = new Uint8Array(regionWidth * regionHeight)
    const mainBuffer = this.buffer
    const mainBufferWidth = this.width

    for (let row = 0; row < regionHeight; row++) {
      const sourceY = yMin + row
      const sourceStartIndex = sourceY * mainBufferWidth + xMin
      const subBufferStartIndex = row * regionWidth
      const rowData = mainBuffer.subarray(sourceStartIndex, sourceStartIndex + regionWidth)
      subBuffer.set(rowData, subBufferStartIndex)
    }

    return {
      x: xMin,
      y: yMin,
      width: regionWidth,
      height: regionHeight,
      data: subBuffer,
    }
  }

  public setActiveBuffer(buffer: Uint8Array): void {
    this.buffer.set(buffer)
  }

  /**
   * Takes a snapshot of the current state of the Raster.
   * This is only returning the snapshot, not setting it in any way.
   * Include the buffer, and data mappings to link annotations, labels and classes
   */
  public takeSnapshot(): RasterDataSnapshot {
    const snapshotData: RasterDataSnapshot = {
      denseRLE: encodeDenseRLE(this.getBufferForSerialization()),
      maskAnnotationIdsMapping: structuredClone(toRaw(this.getAnnotationToLabelMapping())),
      maskAnnotationClassIdsMapping: structuredClone(toRaw(this.getAnnotationIdToClassIdMapping())),
      labelIndexToClassIdMapping: structuredClone(toRaw(this.getLabelIndexToClassIdMapping())),
      labelIndexToAnnotationIdMapping: structuredClone(
        toRaw(this.getLabelIndexToAnnotationMapping()),
      ),
      maskAnnotations: JSON.parse(JSON.stringify(this.getAnnotations())),
    }
    return snapshotData
  }

  /**
   * When clearing the mappings, we also need to clear the bounds as they would be linked
   * to labels that are potentially no longer mapped
   * TODO: Can `cleanup` be used instead? To be checked with
   * https://linear.app/v7labs/issue/DAR-1879/add-create-mask-action-function-for-image-rasters
   */
  clearAnnotationMappings(): void {
    super.clearAnnotationMappings()
    this.boundsPerLabelIndex = {}
  }

  protected async updateLabelOnRaster(
    labelIndex: number,
    updatedLabelIndex: number,
  ): Promise<void> {
    const { width, height } = this

    const labelBounds = this.getBoundsForLabelIndex(labelIndex)
    if (!labelBounds) {
      return
    }

    const xMin = labelBounds.topLeft.x
    const xMax = labelBounds.bottomRight.x
    const yMin = labelBounds.topLeft.y
    const yMax = labelBounds.bottomRight.y

    if (this.view.editor.featureFlags.BRUSH_WORKER) {
      let brushWorker: BrushWorker | null = null
      // Initialize worker for offloading heavy operations
      try {
        brushWorker = new BrushWorker()

        const buffer = this.sharedBuffer || this.buffer.buffer
        const result = await brushWorker.updateLabel(
          buffer,
          width,
          height,
          labelIndex,
          updatedLabelIndex,
          { xMin, xMax, yMin, yMax },
        )

        if (result) {
          // If worker doesn't support SharedArrayBuffer, copy the result back
          if (result.buffer) {
            this.buffer.set(result.buffer)
          }

          // Update bounds based on actual modified region
          const { bounds } = result
          if (bounds.maxX >= bounds.minX && bounds.maxY >= bounds.minY) {
            this.invalidate(bounds.minX, bounds.maxX, bounds.minY, bounds.maxY)
          }
        }

        this.deleteBoundsForLabelIndex(labelIndex)
        this.deleteAnnotationMapping(labelIndex)

        return
      } catch (error) {
        // Worker failed, fall back to main thread
        console.warn('Worker failed for updateLabelOnRaster, falling back to main thread:', error)
      } finally {
        brushWorker?.cleanup()
        brushWorker = null
      }
    }

    // Fallback to main thread implementation
    for (let y = yMin; y < yMax; y++) {
      for (let x = xMin; x < xMax; x++) {
        const pixelIndex = y * width + x

        if (this.buffer[pixelIndex] === labelIndex) {
          this.buffer[pixelIndex] = updatedLabelIndex
        }
      }
    }

    this.deleteBoundsForLabelIndex(labelIndex)
    this.deleteAnnotationMapping(labelIndex)
    this.invalidate(xMin, xMax, yMin, yMax)
  }

  public async deleteLabelFromRaster(labelIndex: number): Promise<void> {
    mark(RASTER_DELETE_LABEL_FROM_RASTER_START)
    await this.updateLabelOnRaster(labelIndex, EMPTY_PIXEL)
    mark(RASTER_DELETE_LABEL_FROM_RASTER_END)
  }

  cleanup(): void {
    super.cleanup()
    // This will drop the reference and be garbage collected soon when the manager drops
    // the reference but making a new empty array means we don't need to make this
    // property optional.
    this.buffer.set(new Uint8Array(0))
    this.boundsPerLabelIndex = {}

    this.cachedCanvas?.remove()
    delete this.cachedCanvas

    this.snapshot = undefined
  }

  /**
   * Checks and removes empty masks after raster operations
   * This should be called after any raster modification that might result in empty masks
   */
  public async checkAndRemoveEmptyMasks(labelsBeingOverwritten: number[]): Promise<void> {
    const { annotationManager } = this.view
    const labelsToDelete: number[] = []
    const annotationsToRemove: string[] = []

    const rasterBufferAccessor = this.getActiveBufferForEdit()

    labelsBeingOverwritten.forEach((labelIndex: number) => {
      const annotationId = this.getAnnotationMapping(labelIndex)
      if (!annotationId) return

      const annotation = annotationManager.getAnnotation(annotationId)
      if (!annotation || !isMaskAnnotation(annotation)) return

      const bounds = this.getBoundsForLabelIndex(labelIndex)

      // Check if the mask is empty
      if (isMaskAnnotationEmpty(labelIndex, rasterBufferAccessor, bounds)) {
        labelsToDelete.push(labelIndex)
        annotationsToRemove.push(annotationId)
      }
    })

    // Delete labels from raster first (this uses worker and is properly awaited)
    for (const labelIndex of labelsToDelete) {
      await this.deleteLabelFromRaster(labelIndex)
    }

    // Then remove annotations from annotation manager (this is just metadata cleanup)
    annotationsToRemove.forEach((annotationId) => {
      annotationManager.deleteAnnotation(annotationId)
    })
  }
}
